/**
 * Order a campaign, v5: every Beatly offering (influencer marketing, UGC, seedings, ambassadors, boosts, paid social,
 * CTV, DOOH, display, online video, audio, brand lift) alone or combined, in four steps: Outcome, Mix, Plan, Review
 * and order. Lykke sits on the left with how ready the order is (every kickoff field and beat attribute has a source);
 * prices, the split, the promise and readiness all come from cowork /v1/campaign-order/v5.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Check, Loader2, SendHorizontal } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { useEffectiveBrand } from '@/cowork/useActiveBrand';
import type { OrderPrepare } from '../types';
import type { Catalogue, OrderV5Result, PlanV5 } from './types';
import { bodyV5, chosenIdea, fmt, initialV5, STEPS, type Step, type V5State } from './state';
import { PartIcon } from './Visuals';
import { MixStep, OutcomeStep } from './StepsStart';
import { PlanStep } from './PlanStep';
import { DoneV5, ReviewStep } from './ReviewStep';
import { StepsSlot } from './styles';

type ThreadItem = { kind: 'msg'; who: 'lykke' | 'user'; text: string } | { kind: 'wrote'; field: string; value: string; step: Step };

export function OrderFlowV5() {
  const { brandId } = useEffectiveBrand();
  const prepQ = useQuery({ queryKey: ['campaign-order', 'prepare', brandId], queryFn: () => coworkApi.campaignOrder.prepare(brandId!), enabled: !!brandId, staleTime: 5 * 60_000, retry: false });
  const catQ = useQuery({ queryKey: ['campaign-order', 'v5-catalogue', brandId], queryFn: () => coworkApi.campaignOrder.v5.catalogue(brandId!), enabled: !!brandId, staleTime: 10 * 60_000, retry: false });

  if (!brandId) return <Centered>Pick a brand in the top bar to order a campaign.</Centered>;
  if (prepQ.isLoading || catQ.isLoading) return <Centered><Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />Reading your Brand OS</Centered>;
  const err = prepQ.error || catQ.error;
  if (err || !prepQ.data || !catQ.data) return <Centered>{(err as Error)?.message || 'Ordering a campaign is not available right now.'}</Centered>;
  return <Flow key={brandId} prep={prepQ.data} cat={catQ.data} />;
}
const Centered = ({ children }: { children: React.ReactNode }) => <div className="grid min-h-[60vh] place-items-center px-4 text-center text-sm text-[#667085]"><p className="max-w-md">{children}</p></div>;
const toTop = () => { document.getElementById('order-v5')?.scrollTo({ top: 0, behavior: 'smooth' }); window.scrollTo({ top: 0, behavior: 'smooth' }); };

const OPENERS: Record<Step, string> = {
  0: 'Tell me what you want to get out there, and what it should achieve. I suggest the mix.',
  1: 'Here is the mix I suggest for that. Take it, or add and remove parts. Each part has its own goal.',
  2: 'Your plan: when, how much, who it is for and the idea. Everything adds up as you change it.',
  3: 'I filled what I could from your Brand OS and your plan. Check it and place the order.',
};

function Flow({ prep, cat }: { prep: OrderPrepare; cat: Catalogue }) {
  const [s, setS] = useState<V5State>(() => initialV5(prep, cat));
  const sRef = useRef(s); sRef.current = s;
  const [step, setStep] = useState<Step>(0);
  const [maxStep, setMaxStep] = useState<Step>(0);
  const stepRef = useRef(step); stepRef.current = step;
  const [thread, setThread] = useState<ThreadItem[]>([{ kind: 'msg', who: 'lykke', text: OPENERS[0] }]);
  const say = (text: string, who: 'lykke' | 'user' = 'lykke') => setThread((t) => [...t, { kind: 'msg', who, text }]);

  const set = useCallback((patch: Partial<V5State>, w?: { field: string; value: string }) => {
    setS((x) => ({ ...x, ...patch }));
    if (w) setThread((t) => {
      const last = t[t.length - 1];
      const item: ThreadItem = { kind: 'wrote', field: w.field, value: w.value, step: stepRef.current };
      return last?.kind === 'wrote' && last.field === w.field ? [...t.slice(0, -1), item] : [...t, item];
    });
  }, []);

  const canLeave = (st: Step, x: V5State) => (st === 0 ? !!x.outcome : st === 1 ? x.parts.length > 0 : true);
  const goTo = (st: Step) => {
    if (st > maxStep && !(st === maxStep + 1 && canLeave(maxStep, sRef.current))) return;
    setStep(st);
    if (st > maxStep) { setMaxStep(st); say(OPENERS[st]); }
    toTop();
  };

  // ── The plan: priced by the API whenever the order changes (debounced) ─────────────────────────────────────
  const [plan, setPlan] = useState<PlanV5 | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const body = useMemo(() => (s.parts.length ? bodyV5(s, prep, cat) : null), [s, prep, cat]);
  const bodyKey = JSON.stringify(body);
  useEffect(() => {
    if (!body) { setPlan(null); return; }
    setPlanLoading(true);
    let live = true;
    const t = setTimeout(() => {
      coworkApi.campaignOrder.v5.plan(body).then((r) => { if (live) setPlan(r); }).catch(() => undefined).finally(() => { if (live) setPlanLoading(false); });
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [bodyKey]);

  // ── Order ──────────────────────────────────────────────────────────────────────────────────────────────────
  const [ordering, setOrdering] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [result, setResult] = useState<OrderV5Result | null>(null);
  const placeOrder = () => {
    setOrdering(true); setOrderError(null);
    coworkApi.campaignOrder.v5.order(bodyV5(sRef.current, prep, cat))
      .then((r) => { setResult(r); say('Ordered. Your campaign manager confirms within one working day; I keep you posted here and in Messages.'); toTop(); })
      .catch((e) => setOrderError((e as Error).message))
      .finally(() => setOrdering(false));
  };

  // ── Lykke by text: writes only what the user stated ───────────────────────────────────────────────────────
  const [busy, setBusy] = useState(false);
  const onSend = (text: string) => {
    say(text, 'user'); setBusy(true);
    const x = sRef.current;
    const history = thread.filter((t): t is Extract<ThreadItem, { kind: 'msg' }> => t.kind === 'msg').slice(-8).map((t) => ({ role: t.who, text: t.text }));
    const state = { outcome: x.outcome, parts: x.parts.map((id) => cat.parts.find((p) => p.id === id)!.title), product: x.productName, market: x.market, budget: x.budget, start: x.start, weeks: x.weeks, idea: chosenIdea(x)?.title, delivery: x.delivery, approverEmail: x.approverEmail, stillNeeded: (plan?.asks || []).map((a) => a.label), total: plan ? `${fmt(plan.bill.excl)} ${plan.bill.currency} excl. VAT` : undefined };
    coworkApi.campaignOrder.converse({ brandId: prep.brand.id, message: text, state, history })
      .then((r) => {
        const v = r.set || {};
        const patch: Partial<V5State> = {}; const w: Array<{ field: string; value: string }> = [];
        if (v.market && prep.markets.some((m) => m.id === v.market)) { patch.market = v.market; w.push({ field: 'Market', value: prep.markets.find((m) => m.id === v.market)!.name }); }
        if (typeof v.budget === 'number' && v.budget > 0) { patch.budget = Math.round(v.budget); w.push({ field: 'Budget', value: fmt(v.budget) }); }
        if (v.start && /^\d{4}-\d{2}-\d{2}$/.test(v.start)) { patch.start = v.start; w.push({ field: 'Start', value: v.start }); }
        if (v.delivery && prep.delivery.some((d) => d.id === v.delivery)) { patch.delivery = v.delivery; w.push({ field: 'Delivery', value: v.delivery }); }
        if (v.approverEmail) { patch.approverEmail = v.approverEmail; w.push({ field: 'Approver', value: v.approverEmail }); }
        if (v.product) { patch.productName = v.product; w.push({ field: 'Product', value: v.product }); }
        if (v.audience) {
          const a = prep.audiences.find((x2) => x2.name.toLowerCase() === v.audience!.toLowerCase());
          if (a) patch.audienceIds = [...new Set([...sRef.current.audienceIds, a.id])]; else patch.audienceText = v.audience;
          w.push({ field: 'Audience', value: v.audience });
        }
        if (v.note) { patch.brief = [sRef.current.brief, v.note].filter(Boolean).join('\n'); w.push({ field: 'Note', value: v.note }); }
        if (Object.keys(patch).length) setS((y) => ({ ...y, ...patch }));
        if (w.length) setThread((t) => [...t, ...w.map((x2) => ({ kind: 'wrote' as const, ...x2, step: stepRef.current }))]);
        if (r.say) say(r.say);
      })
      .catch((e) => say((e as Error).message))
      .finally(() => setBusy(false));
  };

  const stepProps = { prep, cat, s, set };
  return (
    <div className="flex flex-col lg:h-[calc(100vh-3.5rem)] lg:flex-row lg:overflow-hidden">
      <LykkePanel plan={plan} thread={thread} busy={busy} onSend={onSend} onJump={goTo} />
      <div className="flex min-w-0 flex-1 flex-col lg:h-full">
        <main id="order-v5" className="min-w-0 flex-1 bg-[#f8f9fa] px-4 pb-10 pt-6 sm:px-8 lg:pt-8 lg:overflow-y-auto">
          <div className="mx-auto flex max-w-[1000px] flex-col gap-6">
            {result ? <DoneV5 result={result} /> : (
              <>
                <StepsSlot.Provider value={<nav aria-label="Steps" className="flex flex-wrap items-center gap-1.5">
                  {STEPS.map((label, i) => {
                    const st = i as Step; const current = st === step; const reachable = st <= maxStep; const done = st < maxStep && !current;
                    return (
                      <span key={label} className="flex items-center gap-1.5">
                        {i > 0 && <span className="h-px w-4 bg-[#eaecf0]" aria-hidden />}
                        <button type="button" disabled={!reachable} onClick={() => goTo(st)} aria-current={current ? 'step' : undefined}
                          className={cn('inline-flex items-center gap-1.5 text-[13px]', current ? 'text-[#101828]' : 'text-[#667085]', !reachable && 'opacity-50')} style={{ fontWeight: current ? 600 : 500 }}>
                          <span className={cn('grid h-5 w-5 place-items-center rounded-full text-[11px]', current ? 'bg-[#2e024c] text-white' : done ? 'bg-[#be43ff]/8 text-[#be43ff]' : 'border border-[#d0d5dd] text-[#667085]')} style={{ fontWeight: 600 }}>{done ? <Check className="h-3 w-3" aria-hidden /> : i + 1}</span>{label}
                        </button>
                      </span>
                    );
                  })}
                </nav>}>
                {step === 0 && <OutcomeStep {...stepProps} />}
                {step === 1 && <MixStep {...stepProps} />}
                {step === 2 && <PlanStep {...stepProps} plan={plan} planLoading={planLoading} />}
                {step === 3 && <ReviewStep {...stepProps} plan={plan} onStep={goTo} onOrder={placeOrder} ordering={ordering} orderError={orderError} />}
                </StepsSlot.Provider>
              </>
            )}
          </div>
        </main>
        {!result && (
          <footer className="sticky bottom-0 flex items-center gap-3 border-t border-[#eaecf0] bg-white px-4 py-3 sm:px-8">
            <div className="hidden -space-x-1.5 sm:flex">{s.parts.map((id) => <PartIcon key={id} id={id} size={26} filled />)}</div>
            <div className="min-w-0 flex-1 text-[12px] text-[#667085]">
              {plan && s.parts.length ? <><b className="text-[14px] tabular-nums text-[#101828]">{s.parts.length} part{s.parts.length > 1 ? 's' : ''} · {fmt(plan.bill.excl)} {plan.bill.currency}</b><span className="block">excl. VAT · ready {plan.readiness.length - plan.asks.length} of {plan.readiness.length}</span></> : 'Pick an outcome to start'}
            </div>
            {step > 0 && <button type="button" onClick={() => goTo((step - 1) as Step)} className="h-11 rounded-xl border border-gray-200 bg-white px-5 text-[13px] text-[#344054] shadow-sm transition-colors hover:bg-gray-50" style={{ fontWeight: 600 }}>Back</button>}
            {step < 3 && (
              <button type="button" disabled={!canLeave(step, s)} onClick={() => goTo((step + 1) as Step)} className="inline-flex h-11 items-center gap-2 rounded-xl bg-[#be43ff] px-6 text-sm text-white transition-all hover:bg-[#a830e0] active:scale-[0.97] disabled:opacity-40" style={{ fontWeight: 600 }}>
                {['Next: your mix', 'Next: the plan', 'Next: review'][step]}<ArrowRight className="h-4 w-4" aria-hidden />
              </button>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

/** Lykke on the left: how ready the order is (what is still needed), the conversation, and the composer. */
function LykkePanel({ plan, thread, busy, onSend, onJump }: { plan: PlanV5 | null; thread: ThreadItem[]; busy: boolean; onSend: (t: string) => void; onJump: (s: Step) => void }) {
  const [draft, setDraft] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);
  useEffect(() => { threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' }); }, [thread.length]);
  const send = () => { const t = draft.trim(); if (!t || busy) return; setDraft(''); onSend(t); };
  const total = plan?.readiness.length || 0, ready = total - (plan?.asks.length || 0);
  return (
    <aside aria-label="Lykke, your account manager" className="flex min-h-0 flex-col border-b border-gray-200 bg-white lg:h-full lg:w-[340px] lg:flex-none lg:border-b-0 lg:border-r">
      <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
        <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-[#2E024C] text-sm text-white" style={{ fontWeight: 600 }} aria-hidden>L</span>
        <div className="min-w-0 flex-1"><div className="text-sm text-[#101828]" style={{ fontWeight: 600 }}>Lykke</div><div className="text-xs text-[#667085]">Your account manager at Beatly</div></div>
      </div>
      <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-3">
        <div className="flex items-baseline justify-between"><span className="text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>Ready to order</span><span className="text-xs tabular-nums text-[#667085]">{total ? `${ready} of ${total}` : '·'}</span></div>
        <div className="h-1.5 overflow-hidden rounded-full bg-gray-100" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={ready} aria-label="Order readiness">
          <div className="h-full rounded-full bg-[#BE43FF] transition-all" style={{ width: total ? `${(ready / total) * 100}%` : '0%' }} />
        </div>
        {plan && (plan.asks.length
          ? <><div className="flex flex-wrap gap-1.5">{plan.asks.map((a) => <button key={a.label} type="button" onClick={() => onJump(3)} className="rounded-lg bg-[#be43ff]/8 px-2 py-0.5 text-[11px] text-[#be43ff] hover:bg-[#be43ff]/15">{a.label}</button>)}</div>
            <p className="text-[11.5px] text-[#667085]">Answer these at the review, or tell me now. {plan.after.length} more come after the order.</p></>
          : <p className="text-[11.5px] text-[#667085]">Everything needed to order is in. {plan.after.length} things come after the order.</p>)}
      </div>
      <div ref={threadRef} className="flex min-h-[160px] flex-1 flex-col gap-2.5 overflow-y-auto px-4 py-3 [scrollbar-width:none]" aria-live="polite">
        {thread.map((t, i) => t.kind === 'msg' ? (
          <p key={i} className={cn('max-w-[92%] rounded-2xl px-3 py-2 text-[13.5px] leading-relaxed', t.who === 'lykke' ? 'self-start rounded-bl-md bg-gray-100 text-[#101828]' : 'self-end rounded-br-md bg-[#BE43FF] text-white')}>{t.text}</p>
        ) : (
          <button key={i} type="button" onClick={() => onJump(t.step)} className="inline-flex items-start gap-1.5 self-start text-left text-xs text-[#0e9384] hover:underline">
            <Check className="mt-px h-3.5 w-3.5 flex-none" aria-hidden /><span>Wrote <span className="text-[#101828]" style={{ fontWeight: 600 }}>{t.field}:</span> {t.value}</span>
          </button>
        ))}
        {busy && <p className="self-start rounded-2xl rounded-bl-md bg-gray-100 px-3 py-2 text-[13px] text-[#667085]"><Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" aria-hidden />Lykke is writing</p>}
      </div>
      <form className="flex items-end gap-2 border-t border-gray-200 px-4 py-3" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <label htmlFor="lykke-v5" className="sr-only">Message Lykke</label>
        <textarea id="lykke-v5" value={draft} onChange={(e) => setDraft(e.target.value)} rows={1} placeholder="Type to Lykke"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="max-h-32 min-h-[42px] flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#BE43FF]" />
        <button type="submit" disabled={!draft.trim() || busy} aria-label="Send" className="grid h-[42px] w-[42px] flex-none place-items-center rounded-xl bg-[#BE43FF] text-white disabled:opacity-40"><SendHorizontal className="h-4 w-4" aria-hidden /></button>
      </form>
    </aside>
  );
}
